import { useEffect, useRef, useState } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy from '@/i18n/ns/storefront';
import { WorkspaceIcon } from '../icons';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import {
  useStorefrontApi,
  type ConversationFilter,
  type ConversationSummary,
  type Outcome,
  type OverviewScope,
  type StoreStats,
  type StoreSummary,
} from './api';
import { conversationAsk, type AskChat } from './ask-chat';
import { AnswerText } from './answer-text';
import { shopPrice } from './shop-price';
import {
  creditNumber,
  creditsPhrase,
  percent,
  signedPercent,
  tenth,
  underTenth,
  weekday,
  wholeMinutes,
  when,
} from './format';
import { loadConversations } from './scope-data';
import { useRemote } from './use-storefront';

const FILTERS: ConversationFilter[] = ['all', 'cart', 'checkout', 'person'];

/**
 * Every conversation in the page's scope: one assistant or all of them, one market or all. The week's
 * numbers are on the Overview; the filters carry its counts. Across assistants a row names its
 * assistant, and across markets it carries the market's code.
 */
export function ConversationsTab({
  scope,
  stores,
  onAsk,
}: {
  scope: OverviewScope;
  stores: StoreSummary[];
  onAsk: (ask: AskChat) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const api = useStorefrontApi();
  const key = `${scope.store}:${scope.market}`;
  const locale = useAppCatalogLocale() ?? 'en';
  const stats = useRemote(() => api.overview(scope, 7, locale), `${key}:${locale}`);
  const [filter, setFilter] = useState<ConversationFilter>('all');
  const list = useRemote(() => loadConversations(api, scope, filter), `${key}:${filter}`);
  // Older conversations come a page at a time, with the server's cursor, when asked for; a new
  // scope or filter starts again from the newest.
  const [older, setOlder] = useState<{ items: ConversationSummary[]; next: string | null } | null>(
    null,
  );
  const [olderState, setOlderState] = useState<'idle' | 'loading' | 'failed'>('idle');
  const asking = useRef(`${key}:${filter}`);
  asking.current = `${key}:${filter}`;
  useEffect(() => {
    setOlder(null);
    setOlderState('idle');
  }, [key, filter]);
  const next = older ? older.next : (list.data?.next ?? null);
  const showOlder = async () => {
    if (!next) return;
    const askedFor = asking.current;
    setOlderState('loading');
    try {
      const page = await loadConversations(api, scope, filter, next);
      if (asking.current !== askedFor) return;
      setOlder((current) => ({
        items: [...(current?.items ?? []), ...page.items],
        next: page.next,
      }));
      setOlderState('idle');
    } catch {
      if (asking.current === askedFor) setOlderState('failed');
    }
  };
  const [picked, setPicked] = useState<string | null>(null);
  const storeOf = (id: string | undefined) =>
    stores.find((entry) => entry.id === (id ?? scope.store)) ?? null;
  const many = (id: string | undefined) => (storeOf(id)?.markets?.length ?? 0) > 1;
  const items = [...(list.data?.items ?? []), ...(older?.items ?? [])];
  const selectedId =
    picked && items.some((item) => item.id === picked) ? picked : (items[0]?.id ?? null);
  const filterLabel: Record<ConversationFilter, string> = {
    all: copy.filterAll,
    cart: copy.filterCart,
    checkout: copy.filterCheckout,
    person: copy.filterPerson,
  };
  const filterCount: Record<ConversationFilter, number | undefined> = {
    all: stats.data?.conversations,
    cart: stats.data?.addedToCart,
    checkout: stats.data?.reachedCheckout,
    person: stats.data?.askedForPerson,
  };
  const selected = items.find((item) => item.id === selectedId) ?? null;
  const selectedStore = storeOf(selected?.store);
  return (
    <div className="sf-convs">
      <div className="sf-convs-cols">
        <section className="sf-list" aria-label={copy.tabConversations}>
          <div className="sf-filters" role="group" aria-label={copy.tabConversations}>
            {FILTERS.map((name) => (
              <button
                key={name}
                className="sf-filter"
                data-testid="sf-filter"
                aria-pressed={filter === name}
                onClick={() => setFilter(name)}
              >
                {filterLabel[name]}
                {filterCount[name] !== undefined && <em>{filterCount[name]}</em>}
              </button>
            ))}
            {/* The counts are the week's, as on the Overview; the list below goes further back. */}
            <span className="sf-filters-note">{copy.filterCountsWeek}</span>
          </div>
          {list.error ? (
            <ErrorState message={copy.loadFailed} onRetry={list.refresh} />
          ) : !list.data ? (
            <LoadingState label={copy.loading} />
          ) : !items.length ? (
            <EmptyState
              icon="chat"
              title={copy.noConversations}
              description={copy.noConversationsBody}
            />
          ) : (
            // While another filter or scope loads, the last list stays, dimmed.
            <>
              <ul className="sf-rows" aria-busy={list.loading || undefined}>
                {items.map((item) => (
                  <li key={item.id}>
                    <ConversationRow
                      item={item}
                      selected={item.id === selectedId}
                      assistant={scope.store === 'all' ? (storeOf(item.store)?.name ?? null) : null}
                      market={
                        scope.market === 'all' &&
                        item.market &&
                        (scope.store === 'all' || many(item.store))
                          ? item.market
                          : null
                      }
                      onPick={() => setPicked(item.id)}
                    />
                  </li>
                ))}
              </ul>
              {next && (
                <div className="sf-older">
                  <button
                    className="ws-button"
                    data-testid="sf-older"
                    disabled={olderState === 'loading'}
                    aria-busy={olderState === 'loading' || undefined}
                    onClick={showOlder}
                  >
                    {copy.showOlder}
                  </button>
                  {olderState === 'failed' && <small role="status">{copy.loadFailed}</small>}
                </div>
              )}
            </>
          )}
        </section>
        <section className="sf-transcript" aria-label={copy.tabConversations}>
          {selected && selectedStore ? (
            <Transcript
              storeId={selectedStore.id}
              storeName={selectedStore.name}
              conversationId={selected.id}
              onAsk={onAsk}
            />
          ) : (
            <p className="sf-pick">{copy.pickConversation}</p>
          )}
        </section>
      </div>
    </div>
  );
}

/** The week's numbers: conversations with a bar for each day, then cart, checkout, person and cost. */
export function Kpis({ stats }: { stats: StoreStats }) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const [hover, setHover] = useState<number | null>(null);
  const change = signedPercent(stats.conversations, stats.previous, locale);
  const max = Math.max(1, ...stats.byDay.map((day) => day.count));
  const last = stats.byDay.length - 1;
  const hovered = hover === null ? null : stats.byDay[hover];
  const perChat = stats.conversations ? stats.costCreditMicros / stats.conversations : 0;
  return (
    <div className="sf-kpis">
      <div className="sf-kpi sf-kpi-main">
        <div>
          <span className="sf-kpi-label">{copy.kpiConversations}</span>
          <span className="sf-kpi-value" data-testid="sf-kpi-value">
            {new Intl.NumberFormat(locale).format(stats.conversations)}
          </span>
          <span className="sf-kpi-note" data-tone={change?.startsWith('▲') ? 'good' : undefined}>
            {hovered
              ? formatCopy(copy.sparkDay, {
                  day: weekday(hovered.date, locale),
                  count: String(hovered.count),
                })
              : change
                ? formatCopy(copy.kpiVsPrevious, { change })
                : ''}
          </span>
        </div>
        <div
          className="sf-spark"
          role="group"
          aria-label={formatCopy(copy.sparkLabel, { days: String(stats.byDay.length) })}
        >
          <div className="sf-spark-bars">
            {stats.byDay.map((day, index) => (
              <button
                key={day.date}
                type="button"
                className="sf-spark-bar"
                data-today={index === last || undefined}
                data-hover={hover === index || undefined}
                aria-label={formatCopy(copy.sparkDay, {
                  day: weekday(day.date, locale),
                  count: String(day.count),
                })}
                onMouseEnter={() => setHover(index)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(index)}
                onBlur={() => setHover(null)}
              >
                <i style={{ height: `${Math.max(3, Math.round((day.count / max) * 100))}%` }} />
              </button>
            ))}
          </div>
          <div className="sf-spark-days" aria-hidden="true">
            {stats.byDay.map((day) => (
              <span key={day.date}>{weekday(day.date, locale, true)}</span>
            ))}
          </div>
        </div>
      </div>
      <Kpi
        label={copy.kpiCart}
        value={stats.addedToCart}
        note={formatCopy(copy.ofConversations, {
          percent: percent(stats.addedToCart, stats.conversations, locale),
        })}
      />
      <Kpi
        label={copy.kpiCheckout}
        value={stats.reachedCheckout}
        note={formatCopy(copy.checkoutNote, {
          percent: percent(stats.reachedCheckout, stats.conversations, locale),
        })}
      />
      <Kpi
        label={copy.kpiPerson}
        value={stats.askedForPerson}
        note={formatCopy(copy.personNote, {
          percent: percent(stats.askedForPerson, stats.conversations, locale),
        })}
      />
      <Kpi
        label={copy.kpiCost}
        value={creditNumber(stats.costCreditMicros, locale)}
        // A conversation uses a fraction of a credit: "Under 0.1 credits per conversation", not "About under …".
        note={
          underTenth(perChat)
            ? formatCopy(copy.costNoteUnder, {
                amount: formatCopy(copy.creditsAmount, { amount: tenth(locale) }),
              })
            : formatCopy(copy.costNote, { amount: creditsPhrase(perChat, locale, copy) })
        }
      />
    </div>
  );
}

function Kpi({ label, value, note }: { label: string; value: number | string; note: string }) {
  const locale = useAppCatalogLocale() ?? 'en';
  return (
    <div className="sf-kpi">
      <span className="sf-kpi-label">{label}</span>
      <span className="sf-kpi-value" data-testid="sf-kpi-value">
        {typeof value === 'number' ? new Intl.NumberFormat(locale).format(value) : value}
      </span>
      <span className="sf-kpi-note">{note}</span>
    </div>
  );
}

function useOutcomeLabel() {
  const copy = useNamespaceCopy(storefrontCopy);
  return (outcome: Outcome) =>
    outcome === 'cart'
      ? copy.outcomeCart
      : outcome === 'checkout'
        ? copy.outcomeCheckout
        : copy.outcomePerson;
}

function Outcomes({ outcomes }: { outcomes: Outcome[] }) {
  const copy = useNamespaceCopy(storefrontCopy);
  const label = useOutcomeLabel();
  if (!outcomes.length)
    return (
      <span className="sf-outcome" data-outcome="answered">
        {copy.outcomeAnswered}
      </span>
    );
  return (
    <>
      {outcomes.map((outcome) => (
        <span key={outcome} className="sf-outcome" data-outcome={outcome}>
          {label(outcome)}
        </span>
      ))}
    </>
  );
}

function ConversationRow({
  item,
  selected,
  assistant,
  market,
  onPick,
}: {
  item: ConversationSummary;
  selected: boolean;
  /** Across assistants: the one the conversation was with. */
  assistant: string | null;
  /** Across markets: the market's code. */
  market: string | null;
  onPick: () => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  return (
    <button
      className="sf-row"
      data-testid="sf-row"
      aria-current={selected || undefined}
      onClick={onPick}
    >
      <b>{item.firstQuestion}</b>
      <span className="sf-row-meta">
        {market && (
          <span className="sf-cc" data-testid="sf-row-market">
            {market.toUpperCase()}
          </span>
        )}
        {assistant && <span className="sf-row-assistant">{assistant}</span>}
        {/* A computer is the usual case; a row names the device only for phones. */}
        {item.device === 'phone'
          ? formatCopy(copy.rowMeta, {
              when: when(item.startedAt, locale),
              device: copy.devicePhone,
            })
          : when(item.startedAt, locale)}
        <Outcomes outcomes={item.outcomes} />
      </span>
    </button>
  );
}

function Transcript({
  storeId,
  storeName,
  conversationId,
  onAsk,
}: {
  storeId: string;
  storeName: string;
  conversationId: string;
  onAsk: (ask: AskChat) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const api = useStorefrontApi();
  const remote = useRemote(
    () => api.conversation(storeId, conversationId).then((answer) => answer.conversation),
    `${storeId}:${conversationId}`,
  );
  const conversation = remote.data;
  if (remote.error) return <ErrorState message={copy.loadFailed} onRetry={remote.refresh} />;
  if (!conversation || conversation.id !== conversationId)
    return <LoadingState label={copy.loading} />;
  const label = (event: Outcome | undefined, text: string) =>
    event === 'checkout' ? copy.eventCheckout : event === 'person' ? copy.eventPerson : text;
  return (
    <>
      <header className="sf-tr-head">
        <div>
          <h2 data-testid="sf-transcript-title">{conversation.firstQuestion}</h2>
          <small>
            {formatCopy(copy.detailMeta, {
              when: when(conversation.startedAt, locale),
              device: conversation.device === 'phone' ? copy.devicePhone : copy.deviceComputer,
              minutes: wholeMinutes(conversation.minutes),
              page: pageLabel(conversation.page),
            })}
          </small>
        </div>
        <span className="sf-tr-cost">
          {creditsPhrase(conversation.costCreditMicros, locale, copy)}
        </span>
      </header>
      <div className="sf-tr-msgs">
        {conversation.messages.map((message, index) =>
          message.role === 'shopper' ? (
            <p key={index} className="sf-tr-shopper">
              {message.text}
            </p>
          ) : message.role === 'event' ? (
            <p
              key={index}
              className="sf-tr-event"
              data-event={message.event}
              data-testid="sf-event"
            >
              <WorkspaceIcon
                name={
                  message.event === 'person'
                    ? 'person'
                    : message.event === 'checkout'
                      ? 'arrow'
                      : 'check'
                }
                size={14}
              />
              {label(message.event, message.text)}
            </p>
          ) : (
            <div key={index} className="sf-tr-assistant">
              <AnswerText text={message.text} />
              {!!message.products?.length && (
                <ul className="sf-tr-products">
                  {message.products.map((product, productIndex) => (
                    <li key={productIndex}>
                      <span className="sf-tr-img">
                        {product.image ? (
                          <img src={product.image} alt="" />
                        ) : (
                          <WorkspaceIcon name="store" size={16} />
                        )}
                      </span>
                      <span>
                        <b>{product.name}</b>
                        <small>
                          {typeof product.price === 'number'
                            ? shopPrice(product.price, product.currency, locale)
                            : product.price}
                        </small>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {!!message.sources?.length && (
                <p className="sf-tr-sources">
                  <span>{copy.lineSources}</span>
                  <span className="sf-tr-source-links">
                    {message.sources.map((source, sourceIndex) => (
                      <a
                        key={sourceIndex}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {source.title}
                      </a>
                    ))}
                  </span>
                </p>
              )}
            </div>
          ),
        )}
      </div>
      <footer className="sf-tr-foot">
        <WorkspaceIcon name="lock" size={13} />
        {copy.anonymous}
        <button
          className="ws-button"
          data-testid="sf-ask-conversation"
          onClick={() =>
            onAsk(
              conversationAsk(
                copy,
                { id: storeId, name: storeName },
                conversationId,
                when(conversation.startedAt, locale),
              ),
            )
          }
        >
          {copy.askConversation}
        </button>
      </footer>
    </>
  );
}

/** Where a conversation started, as a page and not a raw address: host and path, no scheme or end slash. */
function pageLabel(page: string): string {
  try {
    const url = new URL(page);
    const path = url.pathname.replace(/\/$/, '');
    return `${url.host}${path}`;
  } catch {
    return page;
  }
}
